const API_URL = process.env.APP_API_URL || 'https://www.sanatandharmmahasabha.com/api/admin';
const BASE_ENDPOINT = `${API_URL}`;

// Track if we've shown session expired message to avoid multiple alerts
let sessionExpiredShown = false;

// Helper function to get auth headers
const getStoredToken = () => {
  // Allow both session storage (short-lived) and local storage (remember me)
  return sessionStorage.getItem('token') || localStorage.getItem('token');
};

const getAuthHeaders = () => {
  const token = getStoredToken();
  return {
    'Authorization': token ? `Bearer ${token}` : '',
  };
};

// Handle session expiry
const handleSessionExpiry = () => {
  if (!sessionExpiredShown) {
    sessionExpiredShown = true;
    alert('Your session has expired. Please log in again.');
    // Clear stored session info
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    // Redirect to login
    window.location.href = '/';
  }
};

// Reset session expired flag when user logs in again
export const resetSessionFlag = () => {
  sessionExpiredShown = false;
};

// Helper for making API requests
const apiRequest = async (endpoint, options = {}) => {
  try {
    const headers = {
      ...getAuthHeaders(),
      ...options.headers,
    };

    // When sending FormData, let the browser set Content-Type including the boundary.
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
      delete headers['content-type'];
    } else {
      // Ensure JSON payloads send correct content type (many APIs rely on this)
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    const response = await fetch(`${BASE_ENDPOINT}${endpoint}`, {
      ...options,
      headers,
    });

    // Handle 401 Unauthorized - token expired or invalid
    if (response.status === 401) {
      handleSessionExpiry();
      throw new Error('Session expired. Please log in again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'An error occurred' }));
      throw new Error(error.message || 'API request failed');
    }

    // Reset session flag on successful response
    sessionExpiredShown = false;
    return response.json();
  } catch (error) {
    // Don't override our custom session expiry error
    if (error.message.includes('Session expired')) {
      throw error;
    }
    console.error('API Error:', error);
    throw error;
  }
};

// ==================== Auth ====================
export const login = async (credentials) => {
  return apiRequest('/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
};

export const logout = async () => {
  return apiRequest('/logout', { method: 'POST' });
};

// ==================== Users ====================
export const getUsers = () => apiRequest('/users');
export const getUserById = (id) => apiRequest(`/users/${id}`);
export const createUser = (userData) => apiRequest('/users/create', {
  method: 'POST',
  body: JSON.stringify(userData),
});
export const updateUser = (id, userData) => apiRequest(`/users/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(userData),
});
export const deleteUser = (id) => apiRequest(`/users/${id}/delete`, { method: 'DELETE' });
export const resetUserPassword = (id, newPassword) => apiRequest(`/users/${id}/reset-password`, {
  method: 'POST',
  body: JSON.stringify({ password: newPassword }),
});

// ==================== Departments ====================
export const getDepartments = () => apiRequest('/departments');
export const getDepartmentById = (id) => apiRequest(`/departments/${id}`);
export const createDepartment = (data) => apiRequest('/departments/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateDepartment = (id, data) => apiRequest(`/departments/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteDepartment = (id) => apiRequest(`/departments/${id}/delete`, { method: 'DELETE' });

// ==================== Mandirs ====================
export const getMandirs = () => apiRequest('/mandirs');
export const getMandirById = (id) => apiRequest(`/mandirs/${id}`);
export const createMandir = (data) => apiRequest('/mandirs/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateMandir = (id, data) => apiRequest(`/mandirs/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteMandir = (id) => apiRequest(`/mandirs/${id}/delete`, { method: 'DELETE' });

// ==================== Pandits ====================
export const getPandits = () => apiRequest('/pandits');
export const getPanditById = (id) => apiRequest(`/pandits/${id}`);
export const createPandit = (data) => apiRequest('/pandits/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updatePandit = (id, data) => apiRequest(`/pandits/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deletePandit = (id) => apiRequest(`/pandits/${id}/delete`, { method: 'DELETE' });

// ==================== Prayers ====================
export const getPrayers = () => apiRequest('/prayers');
export const getPrayerById = (id) => apiRequest(`/prayers/${id}`);
export const createPrayer = (data) => apiRequest('/prayers/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updatePrayer = (id, data) => apiRequest(`/prayers/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deletePrayer = (id) => apiRequest(`/prayers/${id}/delete`, { method: 'DELETE' });

// ==================== Special Days ====================
export const getSpecialDays = (year) => apiRequest(`/special-days${year ? `?year=${year}` : ''}`);
export const getSpecialDayById = (id) => apiRequest(`/special-days/${id}`);
export const createSpecialDay = (data) => apiRequest('/special-days/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateSpecialDay = (id, data) => apiRequest(`/special-days/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteSpecialDay = (id) => apiRequest(`/special-days/${id}/delete`, { method: 'DELETE' });

// ==================== Daily Prayers ====================
export const getDailyPrayers = () => apiRequest('/daily-prayers');
export const getDailyPrayerById = (id) => apiRequest(`/daily-prayers/${id}`);
export const createDailyPrayer = (data) => apiRequest('/daily-prayers/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateDailyPrayer = (id, data) => apiRequest(`/daily-prayers/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteDailyPrayer = (id) => apiRequest(`/daily-prayers/${id}/delete`, { method: 'DELETE' });

// ==================== Events ====================
export const getEvents = () => apiRequest('/events');
export const getEventById = (id) => apiRequest(`/events/${id}`);
export const createEvent = (data) => apiRequest('/events/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateEvent = (id, data) => apiRequest(`/events/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteEvent = (id) => apiRequest(`/events/${id}/delete`, { method: 'DELETE' });
export const getEventRegistrations = (id) => apiRequest(`/events/${id}/registrations`);
export const removeEventRegistration = (eventId, userId) => apiRequest(`/events/${eventId}/registrations/${userId}/delete`, { method: 'DELETE' });

// ==================== Kinderhuis ====================
export const getKinderhuis = () => apiRequest('/kinderhuis');
export const getKinderhuisById = (id) => apiRequest(`/kinderhuis/${id}`);
export const createKinderhuis = (data) => apiRequest('/kinderhuis/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateKinderhuis = (id, data) => apiRequest(`/kinderhuis/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteKinderhuis = (id) => apiRequest(`/kinderhuis/${id}/delete`, { method: 'DELETE' });

// ==================== Hindi Learning ====================
export const getHindiLearning = () => apiRequest('/hindi-learning');
export const getHindiLearningById = (id) => apiRequest(`/hindi-learning/${id}`);
export const createHindiLearning = (data) => apiRequest('/hindi-learning/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateHindiLearning = (id, data) => apiRequest(`/hindi-learning/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteHindiLearning = (id) => apiRequest(`/hindi-learning/${id}/delete`, { method: 'DELETE' });

// ==================== Quizzes ====================
export const getQuizzes = () => apiRequest('/quizzes');
export const getQuizById = (id) => apiRequest(`/quizzes/${id}`);
export const createQuiz = (data) => apiRequest('/quizzes/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateQuiz = (id, data) => apiRequest(`/quizzes/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteQuiz = (id) => apiRequest(`/quizzes/${id}/delete`, { method: 'DELETE' });

// ==================== Stats ====================
export const getStatsOverview = () => apiRequest('/stats/overview');

// ==================== Dharma Articles ====================
export const getDharmaArticles = () => apiRequest('/dharma-articles');
export const getDharmaArticleById = (id) => apiRequest(`/dharma-articles/${id}`);
export const createDharmaArticle = (data) => apiRequest('/dharma-articles/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateDharmaArticle = (id, data) => apiRequest(`/dharma-articles/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteDharmaArticle = (id) => apiRequest(`/dharma-articles/${id}/delete`, { method: 'DELETE' });

// ==================== Schools (Scholen) ====================
export const getSchools = () => apiRequest('/scholen');
export const getSchoolById = (id) => apiRequest(`/scholen/${id}`);
export const createSchool = (data) => apiRequest('/scholen/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateSchool = (id, data) => apiRequest(`/scholen/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteSchool = (id) => apiRequest(`/scholen/${id}/delete`, { method: 'DELETE' });

// ==================== E-Bibliotheek ====================
export const getEBibliotheek = () => apiRequest('/ebibliotheek');
export const getEBibliotheekById = (id) => apiRequest(`/ebibliotheek/${id}`);
export const createEBibliotheek = (data) => apiRequest('/ebibliotheek/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateEBibliotheek = (id, data) => apiRequest(`/ebibliotheek/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteEBibliotheek = (id) => apiRequest(`/ebibliotheek/${id}/delete`, { method: 'DELETE' });

// ==================== Hoofdbestuur ====================
export const getHoofdbestuur = () => apiRequest('/hoofdbestuur');
export const getHoofdbestuurById = (id) => apiRequest(`/hoofdbestuur/${id}`);
export const createHoofdbestuur = (data) => apiRequest('/hoofdbestuur/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateHoofdbestuur = (id, data) => apiRequest(`/hoofdbestuur/${id}/update`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteHoofdbestuur = (id) => apiRequest(`/hoofdbestuur/${id}/delete`, { method: 'DELETE' });

// ==================== Blogs ====================
export const getBlogs = () => apiRequest('/blogs');
export const getBlogById = (id) => apiRequest(`/blogs/${id}`);
export const createBlog = (data) => apiRequest('/blogs/create', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateBlog = (id, data) => apiRequest(`/blogs/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteBlog = (id) => apiRequest(`/blogs/${id}/delete`, { method: 'DELETE' });

export default {
  login,
  logout,
  // Users
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  resetUserPassword,
  // Departments
  getDepartments,
  getDepartmentById,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  // Mandirs
  getMandirs,
  getMandirById,
  createMandir,
  updateMandir,
  deleteMandir,
  // Pandits
  getPandits,
  getPanditById,
  createPandit,
  updatePandit,
  deletePandit,
  // Prayers
  getPrayers,
  getPrayerById,
  createPrayer,
  updatePrayer,
  deletePrayer,
  // Special Days
  getSpecialDays,
  getSpecialDayById,
  createSpecialDay,
  updateSpecialDay,
  deleteSpecialDay,
  // Events
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,
  // Kinderhuis
  getKinderhuis,
  getKinderhuisById,
  createKinderhuis,
  updateKinderhuis,
  deleteKinderhuis,
  // Hindi Learning
  getHindiLearning,
  getHindiLearningById,
  createHindiLearning,
  updateHindiLearning,
  deleteHindiLearning,
  // Quizzes
  getQuizzes,
  getQuizById,
  createQuiz,
  updateQuiz,
  deleteQuiz,
  // Stats
  getStatsOverview,
  // Dharma
  getDharmaArticles,
  getDharmaArticleById,
  createDharmaArticle,
  updateDharmaArticle,
  deleteDharmaArticle,
  // Schools
  getSchools,
  getSchoolById,
  createSchool,
  updateSchool,
  deleteSchool,
  // E-Bibliotheek
  getEBibliotheek,
  getEBibliotheekById,
  createEBibliotheek,
  updateEBibliotheek,
  deleteEBibliotheek,
  // Hoofdbestuur
  getHoofdbestuur,
  getHoofdbestuurById,
  createHoofdbestuur,
  updateHoofdbestuur,
  deleteHoofdbestuur,
  // Blogs
  getBlogs,
  getBlogById,
  createBlog,
  updateBlog,
  deleteBlog,
};
